import {
  Table,
  Select,
  Row,
  Col,
  DatePicker,
  notification,
  Modal,
  Input,
} from "antd";
import { useContext, useEffect, useState } from "react";
import moment from "moment";

import "./styles.css";

import { Context } from "../../context";
import { Fetch, Post } from "../../fetch";
import {
  convertIdToKey,
  convertSubscriptions,
  handleExportClick,
} from "../../utils";
import ExportButton from "../ExportButton";

const { Column } = Table;
const { Option } = Select;

const dateFormat = "YYYY-MM-DD";
const disabledDate = (current) => {
  return current && current > moment().endOf("day");
};

// Builds the shared filter query segment (creation date range + active
// subscription) used by both the users list and the users export, so the
// export mirrors the list ("what you see is what you export"). Each param is
// included only when set, matching the list's original conditional URL logic.
const buildUsersQuery = (filters) => {
  const startDate = moment(filters.date[0]).isValid()
    ? moment(filters.date[0]).format(dateFormat)
    : null;
  const endDate = moment(filters.date[1]).isValid()
    ? moment(filters.date[1]).format(dateFormat)
    : null;
  const params = [];
  if (startDate && endDate) {
    params.push(`start_date=${startDate}`, `end_date=${endDate}`);
  }
  if (filters.activeSubscription) {
    params.push(`active_subscription=${filters.activeSubscription}`);
  }
  return params.join("&");
};

const ManageUsers = () => {
  const { token, apiUrl, superAdmin } = useContext(Context);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState("");
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [searchValue, setSearchValue] = useState();
  const [filters, setFilters] = useState({
    date: [moment().startOf("month"), moment().endOf("month")],
    activeSubscription: null,
  });
  const [reason, setReason] = useState("");
  const [modalVisible, setModalVisible] = useState(false);

  useEffect(() => {
    const query = buildUsersQuery(filters);
    const url = query ? `${apiUrl}/users?${query}` : `${apiUrl}/users`;

    Fetch(url, token, (result) => {
      const users = convertSubscriptions(convertIdToKey(result.users));
      users.forEach((user) => {
        if (!user.mobile) {
          user.mobile = "-";
        }

        if (!user.email) {
          user.email = "-";
        }
      });
      setUsers(users);
      setPagination((p) => {
        return {
          ...pagination,
          total: result.total,
        };
      });
    });
  }, [apiUrl, token, filters]);

  const handleSearchChange = (searchText) => {
    setSearchValue(searchText);
  };

  const handleTableDataChange = (paginationParam) => {
    setLoading(true);
    const query = buildUsersQuery(filters);
    const pageParams = `current=${paginationParam.current}&size=${paginationParam.pageSize}`;
    const url = query
      ? `${apiUrl}/users?${query}&${pageParams}`
      : `${apiUrl}/users?${pageParams}`;

    Fetch(url, token, (result) => {
      const users = convertSubscriptions(convertIdToKey(result.users));
      users.forEach((user) => {
        if (!user.mobile) {
          user.mobile = "-";
        }

        if (!user.email) {
          user.email = "-";
        }
      });
      setUsers(users);
      setPagination({ ...paginationParam, total: result.total });
      setLoading(false);
    });
  };

  const handleSearch = (searchText) => {
    if (searchText) {
      setLoading(true);
      Fetch(`${apiUrl}/users?search_text=${searchText}`, token, (result) => {
        const users = convertSubscriptions(convertIdToKey(result.users));
        users.forEach((user) => {
          if (!user.mobile) {
            user.mobile = "-";
          }

          if (!user.email) {
            user.email = "-";
          }
        });
        setUsers(users);
        setLoading(false);
      });
    }
  };

  const cancelSubscription = () => {
    try {
      if (!reason) {
        notification.error({
          message: "Cancellation unsuccessful",
          description: "reason is mandatory",
        });
        return;
      }
      setLoading(true);
      Post(
        `${apiUrl}/users/${user}/cancel_subscription`,
        token,
        { reason },
        (result) => {
          if (result.success) {
            notification.success({
              message: "Success",
              description: "Subscription cancelled successfully",
            });
            setFilters({ date: [null, null], activeSubscription: null });
          }
          setLoading(false);
        },
        () => {
          notification.error({
            message: "Error removing subscription",
            description: "Please check console for more details",
          });
          setLoading(false);
        }
      );
    } finally {
      setReason("");
      setModalVisible(false);
    }
  };

  return (
    <div className="manage-episodes-wrapper">
      <Row>
        <Col span={6}>
          <Select
            placeholder="Enter User Mobile or Email"
            showSearch
            allowClear
            value={searchValue}
            onChange={handleSearchChange}
            onSearch={handleSearch}
            notFoundContent={null}
            onClear={() => {
              handleTableDataChange({ current: 1, pageSize: 10 });
              setSearchValue(null);
            }}
            style={{ width: "100%" }}
          />
        </Col>
        <Col span={1} />
        <Col span={4}>
          <Select
            placeholder="Subscription"
            allowClear
            onChange={(e) => {
              setFilters({ ...filters, activeSubscription: e });
              setPagination({ current: 1, pageSize: 10 });
            }}
            notFoundContent={null}
            onClear={() => {
              setFilters({ ...filters, activeSubscription: null });
              setPagination({ current: 1, pageSize: 10 });
            }}
            style={{ width: "100%" }}
          >
            <Option value={"true"}>Yes</Option>
            <Option value={"false"}>No</Option>
          </Select>
        </Col>
        <Col span={1} />
        <Col span={6}>
          <DatePicker.RangePicker
            disabledDate={disabledDate}
            format={dateFormat}
            defaultValue={filters.date}
            onChange={(_, dateString) => {
              setFilters({
                ...filters,
                date: dateString ? dateString : [null, null],
              });
              setPagination({ current: 1, pageSize: 10 });
            }}
            renderExtraFooter={() => "Filter by user creation date"}
          />
        </Col>
      </Row>
      <Table
        dataSource={users}
        loading={loading}
        pagination={pagination}
        onChange={handleTableDataChange}
      >
        <Column
          title="Name"
          dataIndex="name"
          key="name"
          align="center"
          render={(_, record) =>
            record.suspended ? "Deleted User" : record.name
          }
        />
        <Column
          title="Email"
          dataIndex="email"
          key="email"
          align="center"
          render={(_, record) =>
            record.suspended ? "Deleted User" : record.email
          }
        />
        <Column
          title="Mobile"
          dataIndex="mobile"
          key="mobile"
          align="center"
          render={(_, record) =>
            record.suspended ? "Deleted User" : record.mobile
          }
        />
        <Column
          title="Created At"
          dataIndex="createdAt"
          key="createdAt"
          align="center"
          render={(_, record) =>
            record.suspended
              ? "NA"
              : moment(record.createdAt).format(dateFormat)
          }
        />
        <Column
          title="Subscription"
          dataIndex="subscription"
          key="subscription"
          align="center"
          render={(_, record) =>
            record.suspended ? "Deleted User" : record.subscription
          }
        />
        <Column
          title="Actions"
          dataIndex="actions"
          key="actions"
          align="center"
          render={(_, record) =>
            record.suspended ? (
              "NA"
            ) : superAdmin && record.activeSubscription ? (
              <a
                onClick={() => {
                  setUser(record.key);
                  setModalVisible(true);
                }}
              >
                Cancel Subscription
              </a>
            ) : (
              "-"
            )
          }
        />
      </Table>
      <ExportButton
        className="export-btn-only"
        onExport={() =>
          handleExportClick(
            apiUrl,
            token,
            `users?${buildUsersQuery(filters)}`,
            "Users.csv"
          )
        }
      />
      <Modal
        title="Enter reason for cancellation"
        visible={modalVisible}
        onOk={() => cancelSubscription()}
        onCancel={() => {
          setReason("");
          setModalVisible(false);
        }}
        maskClosable={false}
        okText="Confirm Cancellation"
        cancelText="go back"
      >
        <Input onChange={(e) => setReason(e.target.value)} />
      </Modal>
    </div>
  );
};

export default ManageUsers;
